import { z } from 'zod'

import {
  type ConfigPriceFormValues,
  getSegmentMarkupPercent,
  needsRrcColumn,
} from './configPriceModel'

const MAX_PERCENT = 100

type IssueContext = z.RefinementCtx

const isBlank = (value: string) => value.trim() === ''

const isColumnNumber = (value: string) =>
  Number.isInteger(Number(value)) && Number(value) >= 1

const isPercentNumber = (value: string) =>
  !Number.isNaN(Number(value)) && Number(value) >= 0

const addIssue = (ctx: IssueContext, path: string[], message: string) =>
  ctx.addIssue({ code: z.ZodIssueCode.custom, path, message })

const requireColumn = (
  ctx: IssueContext,
  value: string,
  path: string[],
  message: string
) => {
  if (isBlank(value)) addIssue(ctx, path, message)
  else if (!isColumnNumber(value))
    addIssue(ctx, path, 'Номер колонки — целое число от 1')
}

const checkOptionalPercent = (
  ctx: IssueContext,
  value: string,
  path: string[]
) => {
  if (!isBlank(value) && !isPercentNumber(value))
    addIssue(ctx, path, 'Укажите число от 0')
}

const checkPurchase = (values: ConfigPriceFormValues, ctx: IssueContext) => {
  const vat = Number(values.vatRatePercent)
  if (isBlank(values.vatRatePercent) || !(vat >= 0 && vat <= MAX_PERCENT))
    addIssue(ctx, ['vatRatePercent'], 'Ставка НДС — от 0 до 100')
  if (!isBlank(values.purchaseColumnNumber))
    requireColumn(
      ctx,
      values.purchaseColumnNumber,
      ['purchaseColumnNumber'],
      ''
    )
  const discount = Number(values.purchaseDiscountPercent || 0)
  if (!(discount > -MAX_PERCENT && discount < MAX_PERCENT))
    addIssue(
      ctx,
      ['purchaseDiscountPercent'],
      'Скидка — больше −100 и меньше 100'
    )
}

const checkCurrency = (values: ConfigPriceFormValues, ctx: IssueContext) => {
  if (values.currencyFromFile)
    requireColumn(
      ctx,
      values.currencyColumnNumber,
      ['currencyColumnNumber'],
      'Укажите колонку валюты'
    )
  else if (isBlank(values.currencyFixedCode))
    addIssue(ctx, ['currencyFixedCode'], 'Выберите валюту')
}

const checkSegments = (values: ConfigPriceFormValues, ctx: IssueContext) => {
  if (!values.segments.some((row) => row.enabled)) {
    addIssue(ctx, ['segments'], 'Отметьте хотя бы один сегмент')
    return
  }
  if (
    isBlank(values.purchaseColumnNumber) &&
    isBlank(values.rrcColumnNumber) &&
    values.segments.some((row) => row.enabled && row.mode === 'MARKUP')
  )
    addIssue(
      ctx,
      ['segments'],
      'Наценке к закупке нужна колонка закупочной цены или РРЦ'
    )
  values.segments.forEach((row, index) => {
    if (!row.enabled) return
    const path = (field: string) => ['segments', String(index), field]
    if (row.mode === 'COLUMN')
      requireColumn(
        ctx,
        row.columnNumber,
        path('columnNumber'),
        'Укажите колонку цены сегмента'
      )
    if (row.mode === 'RRC_DISCOUNT') {
      const discount = Number(row.deltaPercent || 0)
      if (!(discount >= 0 && discount < MAX_PERCENT))
        addIssue(ctx, path('deltaPercent'), 'Скидка — от 0 до 100')
    }
    if (row.mode === 'MARKUP' && Number.isNaN(Number(row.deltaPercent || 0)))
      addIssue(ctx, path('deltaPercent'), 'Укажите число')
    if (row.mode === 'MARKUP' && row.isRrc && getSegmentMarkupPercent(row) <= 0)
      addIssue(
        ctx,
        path('deltaPercent'),
        'Цена будет равна закупочной: задайте добавку'
      )
  })
}

export const configPriceSchema = z
  .custom<ConfigPriceFormValues>()
  .superRefine((values, ctx) => {
    if (!values.enabled) return
    checkPurchase(values, ctx)
    checkCurrency(values, ctx)
    if (needsRrcColumn(values))
      requireColumn(
        ctx,
        values.rrcColumnNumber,
        ['rrcColumnNumber'],
        'Укажите колонку РРЦ'
      )
    else if (!isBlank(values.rrcColumnNumber))
      requireColumn(ctx, values.rrcColumnNumber, ['rrcColumnNumber'], '')
    checkSegments(values, ctx)
    checkOptionalPercent(ctx, values.minMarkupPercent, ['minMarkupPercent'])
    checkOptionalPercent(ctx, values.spikeAlertPercent, ['spikeAlertPercent'])
    checkOptionalPercent(ctx, values.maxInvalidPricePercent, [
      'maxInvalidPricePercent',
    ])
  })
